import ReactSlider, { ReactSliderProps } from "react-slider";
import { VolumeAllowedValues } from "shared/games/mixpuzzles/types";
import { DOMAttributes, useCallback } from "react";
import classNames from "classnames";
import Button from "ui/atoms/Button/Button";
import { scaleLinear } from "d3";
import times from "util/times";

export interface Props {
  value: number;
  onChange: (value: number) => void;
  theme?: "default" | "white";
  steps: number;
  max?: number;
  min?: number;
}

const marks = 20;
const convertMark = scaleLinear()
  .domain([0, marks - 1])
  .range([10, 90]);

export default function VolumeSlider({
  value,
  onChange,
  theme = "default",
  steps,
  max = 0,
  min = -40,
}: Props) {
  const onReactSliderChange: ReactSliderProps["onChange"] = useCallback(
    (volume: number) => {
      onChange(-volume);
    },
    [onChange]
  );

  return (
    <div
      className={classNames(
        "relative mr-6 mt-7 w-full flex-1 cursor-pointer rounded border-2 px-8",
        {
          "border-slate-300": theme === "default",
          "border-white": theme === "white",
        }
      )}
      onClick={stopPropagation}
    >
      <ReactSlider
        className={"h-12 w-full"}
        thumbClassName={classNames(
          "active:outline-none focus:outline-none top-1"
        )}
        renderThumb={renderThumb({ theme })}
        value={-value}
        onChange={onReactSliderChange}
        orientation="horizontal"
        invert
        step={steps}
        marks={2}
        max={Math.abs(min)}
        min={max}
        renderMark={() => null}
        renderTrack={({ key, ...props }) => (
          <div
            key={key}
            {...props}
            className={classNames(
              "relative top-1/2 h-0.5 -translate-y-1/2 rounded-xl",
              {
                "bg-slate-300": theme === "default",
                "bg-white": theme === "white",
              }
            )}
          />
        )}
      />

      {times(marks, (n) => (
        <span
          key={n}
          className={classNames("r-0.5 absolute -top-7 h-4 translate-x-1/2", {
            "bg-white": theme === "white",
            "bg-slate-300": theme === "default",
          })}
          style={{ width: 1, right: `${convertMark(n)}%` }}
        />
      ))}
    </div>
  );
}

const renderThumb = ({ theme = "default" }: { theme: Props["theme"] }) => {
  const Thumb: ReactSliderProps["renderThumb"] = ({ key, ...props }, state) => (
    <div key={key} {...props}>
      <Button block theme={theme}>
        {-state.valueNow} DB
      </Button>
    </div>
  );

  return Thumb;
};

const stopPropagation: DOMAttributes<HTMLDivElement>["onClick"] = (e) =>
  e.stopPropagation();
